import {
  AfterContentInit,
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { Address } from "@models/address/address";
import { Company } from "@models/companies/company";
import { ChangeAuthorisation, MinutesMeetingAttendee } from "@models/documents/change-authorisation";
import { Document } from "@models/documents/document";
import { DocumentAuthorise } from "@models/documents/document-authorise";
import { DocumentationType, MeetingLocationType, MinutesMeetingPresence } from "@models/documents/document-enums";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { CompanyChangeData } from "@models/ui-models/company-change-data";
import { SelectOption } from "@models/ui-models/select-options";
import { IStep } from "@models/ui-models/step";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import {
  AgSelectChangeNameComponent
} from "@shared/components/grid/ag-cells/bulk-changes/ag-select-change-name/ag-select-change-name.component";
import {
  AgEntityNameAcnAbnComponent
} from "@shared/components/grid/ag-cells/common/ag-entity-name-acn-abn/ag-entity-name-acn-abn.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { TrustsApiService } from "@shared/services/trusts-api.service";
import { enumToSteps } from "@shared/utils/enums-to-list-formatter";
import { Guid } from "@shared/utils/guid.helper";
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { ColDef, RowClassParams, RowClassRules, ValueFormatterParams } from "ag-grid-community";
import { Operation } from "fast-json-patch";
import { ToastrService } from "ngx-toastr";
import { catchError, EMPTY, finalize, Observable, of, startWith, switchMap, tap } from "rxjs";
import {
  fromRelationshipsToSelectOptions,
  fromRelationshipToSelectOption
} from "../../../utils/from-relationship-to-select-option";
import { getDocumentationTypeOptions } from "../../../utils/get-documentation-type-options";
import { StepperFormComponent } from "../../stepper-form";

export abstract class BaseBulkStepperForm<T> {
  isLoading = signal(false);
  steps!: IStep<T>[];
  currentStepIndex!: number;
  currentStep!: T;
  abstract stepperForm: FormGroup;

  abstract setCurrentStep(newStepIndex: number): void;

  abstract buildDocument(): Document[] | null;

  abstract confirm(): void;

  abstract close(isEdit: boolean): void;

  abstract setupChange(): void;
}

export type DocumentBulkSubmitter = OmitThisParameter<(documents: Document[], saveAndCompleteLater?: boolean) => Observable<(Document | null)[]>>;

export interface IBulkAppliedChange {
  name: string;
  entityNumber: string;
  entityType: 'Company' | 'Trust' | null | undefined;
  entityId: string;

  selectedOption: SelectOption;
  options: SelectOption[];

  error?: string,
  warning?: string
}

@Component({
  selector: 'app-base-bulk-stepper-form',
  standalone: true,
  imports: [],
  template: '',
})
export abstract class BaseBulkStepperFormComponent<T, Change extends EntityChangeData> implements BaseBulkStepperForm<T>, AfterContentInit, AfterViewInit {
  @ViewChild(StepperFormComponent) stepperFormComponent!: StepperFormComponent;

  destroyRef = inject(DestroyRef);
  activeModal = inject(NgbActiveModal);
  toastr = inject(ToastrService);
  trustsApiService = inject(TrustsApiService);
  documentsApiService = inject(DocumentsApiService);
  organisationService = inject(OrganisationApiService);
  companiesApiService = inject(CompaniesApiService);
  router = inject(Router);

  @Input() companyChangeData = new CompanyChangeData();
  @Input() formModel!: Change;
  @Input() documentSubmitter: DocumentBulkSubmitter = this.documentsApiService.createOrUpdateBulkDocuments.bind(this.documentsApiService);

  isLoading = signal(false);
  forceClose = false;

  StepsEnum!: any;
  steps!: IStep<T>[];
  currentStepIndex = 0;
  currentStep!: T;
  lateFeeMessage: string | null = null;
  signingManager = this.organisationService.getCurrentUser()?.id ?? Guid.EmptyGuid;
  companies: Company[] = [];

  // Bulks Specials
  documentTypeOptions: SelectOption[] = getDocumentationTypeOptions();

  stepperForm: FormGroup = new FormGroup({});

  abstract afterSubmit(changes: EntityChangeData[]): void;

  ngAfterContentInit() {
    this.activeModal.update({ size: 'xl' });
    this.updateDocumentationTypeOptions();
  }

  ngAfterViewInit(): void {
    this.setupStepperComponentMethods();
  }

  setCurrentStep(newStepIndex: number): void {
    this.currentStep = this.steps[newStepIndex].step;
    this.currentStepIndex = newStepIndex;
  }

  setupChange(): void {
    console.warn('Method not implemented. Override "setupChange" method.');
    console.warn(this);
  }

  saveAndCompleteLater(): void {
    this.submit(true);
  }

  confirm(): void {
    this.submit(false);
  }

  submit(draft = false): void {
    if (this.stepperForm.invalid) {
      this.toastr.error('Please fill required fields', 'Error');
      this.stepperForm.controls[this.currentStep as string]?.markAllAsTouched();

      return;
    }

    const documents = this.buildDocument();

    if (!documents?.length)
      return;

    documents.forEach(document => {
      document.changes.forEach(change => {
        change.changeDate = DatepickerHelper.buildDateString(change.changeDate);
      });
      document.documentStatus = draft ? DocumentStatusEnum.Draft : DocumentStatusEnum.AuthorisationPending;
    });

    this.isLoading.set(true);
    this.stepperForm.disable();

    const isOnlyTrust = documents.every(d => !d.changeAuthorisation);
    const documentsWithoutChangeAuthorization = documents.filter(d => !d.changeAuthorisation);
    if (isOnlyTrust) {
      this.updateTrustAddress(documentsWithoutChangeAuthorization, isOnlyTrust);
      return;
    } else if (documentsWithoutChangeAuthorization.length && documents[0].changes[0].$type === 'CompanyChangeAddress') {
      this.updateTrustAddress(documentsWithoutChangeAuthorization, isOnlyTrust);
    }

    const documentsWithChangeAuthorization = documents.filter(d => d.changeAuthorisation);
    if (documentsWithChangeAuthorization.length) {
      this.documentSubmitter(documentsWithChangeAuthorization, draft)
        .pipe(
          switchMap((createdDocuments: (Document | null)[]) => {
            if (draft) {
              void this.router.navigate(['/documents']);
              return EMPTY;
            } else {
              const filteredDocuments = createdDocuments.filter(Boolean) as Document[];

              return this.documentsApiService.authoriseBulkDocuments(filteredDocuments)
                .pipe(
                  tap((documentAuthorise: (DocumentAuthorise | null)[]) => {
                    if (documentAuthorise.length) {
                      void this.router.navigate(['/documents']);
                    } else {
                      this.toastr.error('Failed to authorise Document.', 'Error');
                    }
                  }),
                );
            }
          }),
          finalize(() => {
            this.toastr.success('Document Saved', 'Success');
            this.activeModal.close(new Document(documents[0]).changes);
            this.stepperForm.enable();
            this.isLoading.set(false);
            this.afterSubmit(documents[0].changes);
          })
        )
        .subscribe({
          error: (error) => {
            console.warn(error);
            this.toastr.error('Failed to save Document.', 'Error');
            this.isLoading.set(false);
          },
        });
    }
  }

  buildDocument(): Document[] | null {
    throw new Error('Method not implemented. Override "buildDocument" method.');
  }

  close(forceClose = false): void {
    this.forceClose = forceClose;

    if (this.isLoading() && !forceClose) {
      alert('Please wait for the operation to complete before closing the modal.');
      return;
    }

    this.activeModal.dismiss(forceClose);
  }

  setupSteps(stepEnum: any): void {
    this.StepsEnum = stepEnum as unknown as Record<string, number>;
    this.steps = enumToSteps(stepEnum);
    this.currentStepIndex = 0;
    this.currentStep = this.steps[this.currentStepIndex].step;
  }

  setupStepperComponentMethods(): void {
    this.takeUntilDestroyed(this.stepperFormComponent.stepIndexChange, this.setCurrentStep.bind(this));
    this.takeUntilDestroyed(this.stepperFormComponent.close, () => this.close(false));
    this.takeUntilDestroyed(this.stepperFormComponent.saveAndCompleteLater, () => this.saveAndCompleteLater());
    this.takeUntilDestroyed(this.stepperFormComponent.confirm, () => this.submit());
  }

  updateTrustAddress(documents: Document[], isOnlyTrust: boolean): void {
    const selectedTrustIds = documents
      .filter(document => document.entityId)
      .map(document => ({ entityId: document.entityId })) as { entityId: string }[];

    const operation: Operation[] = [{
      op: 'replace',
      path: '/registeredAddress',
      value: (documents[0].changes[0] as unknown as { address: Address }).address
    }];

    if (selectedTrustIds.length) {
      this.trustsApiService.bulkPatchOperationTrust(selectedTrustIds, operation).pipe(
        catchError(error => {
          console.warn(error);
          this.toastr.error('Failed to change Trusts Address.', 'Error');
          this.isLoading.set(false);
          return of([]);
        })
      ).subscribe(res => {
        if (res.length && isOnlyTrust) {
          this.toastr.success(
            'The address change for the trust has been successfully updated.',
            'Success',
          );
          this.activeModal.close();
          void this.router.navigate(['/trusts']);
        }
        this.isLoading.set(false);
      });
    }
  }

  takeUntilDestroyed(eventEmitter: EventEmitter<any>, callback: (p: any) => void): void {
    eventEmitter
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(callback);
  }

  listenChangeDateChange(changeDateControl: FormControl<Date | null>, getLateFeeFn: (changeDate: Date) => string | null): void {
    changeDateControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        startWith(changeDateControl.value)
      )
      .subscribe((changeDate) => {
        if (changeDateControl.invalid) {
          this.lateFeeMessage = null;
          return;
        }

        if (!changeDate) return;

        this.lateFeeMessage = getLateFeeFn(changeDate);
      });
  }

  updateDocumentationTypeOptions(): void {
  }

  loadCompany(entityId: string): Observable<Company | null> {
    return this.companiesApiService.getById(entityId, false)
      .pipe(
        catchError((error) => {
          console.warn('[loadCompany] error: ', error);
          return of(null);
        })
      );
  }

  // ===== ===== ===== Handle "Change is applied for" section and it's grid ===== ===== =====

  readonly rowSelection = BaseListGridComponent.noCheckboxesRowSelection;
  readonly rowClassRules: RowClassRules = {
    'bg-red-light': (params: RowClassParams<IBulkAppliedChange>) => Boolean(params.data?.error?.length),
    'bg-primary-light': (params: RowClassParams<IBulkAppliedChange>) => Boolean(params.data?.warning?.length),
  };
  readonly baseColDefs: ColDef[] = [
    {
      headerName: 'Entity Name',
      field: 'name',
      sort: 'asc',
      flex: 0.6,
      cellRenderer: AgEntityNameAcnAbnComponent,
      menuTabs: []
    },
    {
      headerName: 'Authorising Officeholder',
      sortable: false,
      flex: 1,
      cellRenderer: AgSelectChangeNameComponent,
      cellRendererParams: {
        selectName: this.onChangeEntityAuthorisingOfficer.bind(this),
      },
      menuTabs: []
    }
  ];
  readonly errorColDef: ColDef = {
    headerName: 'Error',
    field: 'error',
    flex: 2,
    wrapText: true,
    autoHeight: true,
    valueFormatter: (params: ValueFormatterParams<IBulkAppliedChange>) => params.data?.error || params.data?.warning || '',
    cellClass: 'line-height-20 d-flex align-items-center',
    cellClassRules: {
      'color-red': (params: RowClassParams<IBulkAppliedChange>) => Boolean(params.data?.error?.length),
    },
    menuTabs: []
  };

  colDefs: ColDef[] = this.getColDefs();
  rows: IBulkAppliedChange[] = [];
  isGridIncludesError = false;
  areAllRowsHaveErrors = false;

  getColDefs(showErrorColumn = false): ColDef[] {
    return showErrorColumn
      ? [...this.baseColDefs, this.errorColDef]
      : [...this.baseColDefs];
  }

  fillRows(entities: Company[]): void {
    this.colDefs = [...this.baseColDefs];
    this.rows = entities.map((entity) => {
      const companyDirectors = entity.currentOfficers.filter((officer) => officer.type == RelationshipType.Director);
      const companyDirectorOptions = fromRelationshipsToSelectOptions(companyDirectors);

      return {
        name: entity.name,
        entityNumber: entity.acn,
        entityType: entity.$type as 'Company',
        entityId: entity.entityId,

        selectedOption: companyDirectorOptions[0],
        options: companyDirectorOptions,

        error: '',
        warning: '',
      };
    });
  }

  fillRowsErrors(): void {
    this.rows = this.rows.map((row, index) => {
      row.error = this.getRowError(row, index);
      row.warning = this.getRowWarning(row, index);

      return row;
    });

    this.isGridIncludesError = this.rows.some((row) => row.error || row.warning);
    this.areAllRowsHaveErrors = this.rows.every((row) => row.error);
    this.colDefs = this.getColDefs(this.isGridIncludesError);
  }

  getRowError(row: IBulkAppliedChange, rowIndex: number): string {
    return '';
  }

  getRowWarning(row: IBulkAppliedChange, rowIndex: number): string {
    return '';
  }

  getChangeAuthorisation(entity: Company, selectedDocumentationType: DocumentationType, relatedRow: IBulkAppliedChange, changeDate?: Date): ChangeAuthorisation {
    // Fill common info for the entity
    const companyDirectors = entity.currentOfficers.filter((officer) => officer.type == RelationshipType.Director);
    const authOfficeHolder = companyDirectors[0];
    const signingContact = entity.currentOfficers.find((officer) => officer.contactId == entity.signingContactId) ?? companyDirectors[0];

    const chairperson = companyDirectors[0];
    const authorisingParty = signingContact ? signingContact : authOfficeHolder;
    const signingManager = this.signingManager;
    const documentationType = companyDirectors.length == 1
      ? DocumentationType.Resolution
      : selectedDocumentationType;

    const changeAuthorisation = new ChangeAuthorisation({
      authorisingParty,
      signingManager,
      documentationType,
      chairperson,
      meetingAddressType: MeetingLocationType.Registered,
      meetingAddress: entity.registeredAddress
    });

    if (documentationType == DocumentationType.Minutes && changeDate) {
      changeAuthorisation.meetingDate = DatepickerHelper.buildDateString(changeDate);
      changeAuthorisation.meetingAddressType = MeetingLocationType.Registered;
      changeAuthorisation.meetingAddress = entity.registeredAddress;
      changeAuthorisation.attendees = companyDirectors
        .filter((officer) => officer.individualDataOverride)
        .map(({ individualDataOverride }) => {
          return new MinutesMeetingAttendee({
            individual: individualDataOverride!,
            presence: MinutesMeetingPresence.PresentPhysically
          });
        });
    }

    // Apply selected in row value
    const selectedOfficer = companyDirectors.find((director) => director.relationshipId == relatedRow.selectedOption.value);
    if (selectedOfficer) {
      changeAuthorisation.authorisingParty = selectedOfficer;
    }

    return changeAuthorisation;
  }

  onChangeEntityAuthorisingOfficer(data: { entityId: string, selectedId: string }): void {
    const relatedRow = this.rows.find((row) => row.entityId == data.entityId);
    const relatedChangeAddressEntity = this.companies.find((company) => company.entityId == data.entityId);
    if (!relatedChangeAddressEntity || !relatedRow) return;

    const selectedOfficer = relatedChangeAddressEntity.currentOfficers.find((officer) => officer.relationshipId == data.selectedId);

    if (selectedOfficer) {
      relatedRow.selectedOption = fromRelationshipToSelectOption(selectedOfficer);
    }

    this.fillRowsErrors();
  }
}
